import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Check,
  PersonAdd,
  Inventory,
  Payment,
  Source,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import MarkAsPaidCheckbox from './MarkAsPaidCheckbox';
import { stopWheelNudge } from '../utils/numberInput';
import {
  formatPhoneInput,
  isValidPhoneNumber,
  validatePhoneNumber as validatePhoneField,
} from '../utils/phoneValidation';
import { useAuth } from '../contexts/AuthContext';
import { studioIdOf } from '../utils/studioOf';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchSelect from './BranchSelect';

interface UserInfo {
  _id: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  gender?: string;
}

interface Pack {
  _id: string;
  title: string;
  totalClasses?: number;
  totalPrice?: number;
  durationMonths?: number;
  sessionType?: string;
  studio?: { _id: string } | string | null;
}

interface Studio {
  _id: string;
  studioName: string;
  whitelabelConfig?: { appSource?: string; appName?: string; enableBookingWebsite?: boolean };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studios?: Studio[];
  prefilledUser?: UserInfo;
}

const SS_SOURCE = 'sweatsocial';

const ManualPackAssignDialog: React.FC<Props> = ({ open, onClose, onSuccess, studios = [], prefilledUser }) => {
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';

  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);

  // Under "All locations" a studio role names the location first: the user
  // lookup and the assign are POSTs, which the server refuses for __all__,
  // and the pack list would otherwise span every location.
  const { isAllLocations } = useMyLocations();
  const [branch, setBranch] = useState('');
  const mustPickBranch = !isSuperAdmin && isAllLocations;
  const pinStudioId = mustPickBranch ? branch || undefined : undefined;

  // Step 1: User — phone lookup
  const [phone, setPhone] = useState('');
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [userCheckResult, setUserCheckResult] = useState<{ exists: boolean; user?: UserInfo } | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);

  // Step 1: User — name search
  const [nameQuery, setNameQuery] = useState('');
  const [nameSearchResults, setNameSearchResults] = useState<UserInfo[]>([]);
  const [isSearchingByName, setIsSearchingByName] = useState(false);

  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserGender, setNewUserGender] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState('');

  // Step 2: Pack
  const [packs, setPacks] = useState<Pack[]>([]);
  const [isLoadingPacks, setIsLoadingPacks] = useState(false);
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);

  // Step 3: Source
  const [appSource, setAppSource] = useState<string>(SS_SOURCE);

  // Step 4: Payment
  const [paymentMode, setPaymentMode] = useState<'free' | 'cash' | 'online'>('cash');
  // Cash/online only — unticked stores the pack as unpaid (isPaid: false).
  const [markAsPaid, setMarkAsPaid] = useState(true);
  const [basePrice, setBasePrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  // Server refused because the member reached the pack's purchase limit; staff
  // may confirm and assign anyway (the override is audit-logged server-side).
  const [limitWarning, setLimitWarning] = useState('');
  // The assign alerts sit at the bottom of a long scrolling form; bring them
  // into view when they appear so staff actually see them.
  const assignAlertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (assignError || limitWarning) {
      assignAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [assignError, limitWarning]);
  const [assignSuccess, setAssignSuccess] = useState(false);

  // Derived: active step for the stepper
  const activeStep = !selectedUser ? 0 : !selectedPack ? 1 : appSource === null ? 2 : 3;

  // Effective studio for pack loading
  const studioId = isSuperAdmin
    ? selectedStudio?._id
    : mustPickBranch
      ? pinStudioId
      : (admin as any)?.studio?._id || admin?.id;

  // Studio's whitelabel config
  const studioForSource: Studio | null = isSuperAdmin
    ? selectedStudio
    : { _id: studioId || '', studioName: (admin as any)?.studio?.studioName || '', whitelabelConfig: (admin as any)?.studio?.whitelabelConfig };

  const whitelabelAppSource = studioForSource?.whitelabelConfig?.appSource;
  const whitelabelAppName =
    studioForSource?.whitelabelConfig?.appName || studioForSource?.studioName || '';
  const hasWhitelabel = !!whitelabelAppSource && whitelabelAppSource !== 'sweatsocial';
  // The receipt's look comes from the studio itself, not from the source picked
  // below (EmailService.getBrandingContext): its app name, else its own name
  // when its booking website is on, else SweatSocial.
  const receiptBrandName =
    studioForSource?.whitelabelConfig?.appName ||
    (studioForSource?.whitelabelConfig?.enableBookingWebsite === true ? studioForSource?.studioName : '') ||
    'Sweat Social';
  // The studio's own channel to assign from: its app, or — a studio with no app
  // but its booking website on — that website ('studio_web': the pack then shows
  // on the site; money follows the SweatSocial path, see manualAssignPack).
  const websiteOnly = !hasWhitelabel && studioForSource?.whitelabelConfig?.enableBookingWebsite === true;
  const ownSource: string | null = hasWhitelabel ? whitelabelAppSource! : websiteOnly ? 'studio_web' : null;
  const ownSourceLabel = hasWhitelabel ? whitelabelAppName : studioForSource?.studioName || 'Studio';
  const showSourceStep = !!ownSource && isSuperAdmin;

  useEffect(() => {
    if (!open) {
      resetAll();
      return;
    }
    if (prefilledUser) setSelectedUser(prefilledUser);
  }, [open, prefilledUser]);

  useEffect(() => {
    if (selectedUser && studioId) fetchPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, studioId]);

  // Default source — re-applied on every OPEN, not just when the studio changes.
  //
  // `open` must stay in the deps. Without it this fired once per page load, then
  // resetAll() (on close) forced appSource back to SS_SOURCE and nothing ever
  // restored it: the 2nd and every later assign in that session was silently
  // attributed to Sweat Social instead of the studio's own whitelabel. A studio
  // owner couldn't even see it happen — the source toggle below is admin-only.
  // Confirmed in production (Laya) and reproduced (Purple Pilates): first assign
  // after load → whitelabel, every one after → sweatsocial.
  useEffect(() => {
    if (!open) return;
    setAppSource(ownSource ?? SS_SOURCE);
  }, [open, ownSource]);

  const handleNameSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setNameSearchResults([]); return; }
    setIsSearchingByName(true);
    try {
      if (isSuperAdmin) {
        const res = await apiService.searchUsers(q.trim());
        setNameSearchResults(res?.data || []);
      } else {
        const res = await apiService.getStudioMembers({ search: q.trim(), limit: 10 });
        // Studio members uses 'phone' field — normalise to phoneNumber
        const mapped = (res?.data || []).map((c: any) => ({
          _id: c._id,
          name: c.name,
          email: c.email,
          phoneNumber: c.phone || c.phoneNumber,
        }));
        setNameSearchResults(mapped);
      }
    } catch {
      setNameSearchResults([]);
    } finally {
      setIsSearchingByName(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const t = setTimeout(() => { if (!selectedUser) handleNameSearch(nameQuery); }, 350);
    return () => clearTimeout(t);
  }, [nameQuery, handleNameSearch, selectedUser]);

  const resetAll = () => {
    setBranch('');
    setSelectedStudio(null);
    setPhone('');
    setIsCheckingUser(false);
    setUserCheckResult(null);
    setSelectedUser(null);
    setNameQuery('');
    setNameSearchResults([]);
    setIsSearchingByName(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserGender('');
    setIsCreatingUser(false);
    setCreateUserError('');
    setPacks([]);
    setSelectedPack(null);
    setAppSource(SS_SOURCE);
    setPaymentMode('cash');
    setMarkAsPaid(true);
    setBasePrice('');
    setStartDate('');
    setIsAssigning(false);
    setAssignError('');
    setAssignSuccess(false);
  };

  const getFullPhone = () => `+91${phone.trim().replace(/\D/g, '')}`;

  // Switching location (or studio) fires a new pack load while the previous
  // may still be in flight: only the latest may write the list, or a late
  // answer shows the OLD location's packs under the new one.
  const packsReqRef = useRef(0);
  const fetchPacks = async () => {
    const req = ++packsReqRef.current;
    const stale = () => req !== packsReqRef.current;
    try {
      setIsLoadingPacks(true);
      setPacks([]);
      setSelectedPack(null);

      const sortBySessionType = (list: Pack[]): Pack[] => {
        const order: Record<string, number> = { group: 0, private: 1, duet: 2 };
        return [...list].sort((a, b) => {
          const aOrder = a.sessionType != null ? (order[a.sessionType] ?? 3) : 3;
          const bOrder = b.sessionType != null ? (order[b.sessionType] ?? 3) : 3;
          return aOrder - bOrder;
        });
      };

      if (isSuperAdmin && studioId) {
        // Fetch only studio-specific packs (no global packs)
        const response = await apiService.getPacks({ admin: 'true', studioId });
        if (stale()) return;
        const list: Pack[] = response.packs || [];
        setPacks(sortBySessionType(list));
      } else {
        const response = await apiService.getPacks({ limit: 100 }, pinStudioId);
        if (stale()) return;
        const list = response.packs || response.studioPacks || [];
        setPacks(sortBySessionType(Array.isArray(list) ? list : []));
      }
    } catch {
      if (!stale()) setPacks([]);
    } finally {
      if (!stale()) setIsLoadingPacks(false);
    }
  };

  const handleCheckUser = async () => {
    const phoneError = validatePhoneField(phone);
    if (phoneError) return;
    if (mustPickBranch && !branch) return;
    try {
      setIsCheckingUser(true);
      setUserCheckResult(null);
      setSelectedUser(null);
      const result = await apiService.checkUserByPhoneForPack(getFullPhone(), pinStudioId);
      setUserCheckResult(result);
      if (result.exists && result.user) setSelectedUser(result.user);
    } catch {
      setUserCheckResult({ exists: false });
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) {
      setCreateUserError('Name and email are required');
      return;
    }
    if (mustPickBranch && !branch) {
      setCreateUserError('Choose a location first');
      return;
    }
    try {
      setIsCreatingUser(true);
      setCreateUserError('');
      const result = await apiService.createUserForPackAssign({
        phoneNumber: getFullPhone(),
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        gender: newUserGender || undefined,
      }, pinStudioId);
      setSelectedUser(result.user || result);
    } catch (err: any) {
      setCreateUserError(err?.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleAssignPack = async (overrideLimit = false) => {
    if (!selectedUser || !selectedPack) return;
    try {
      setIsAssigning(true);
      setAssignError('');
      setLimitWarning('');
      const isFree = paymentMode === 'free';
      await apiService.manualAssignPack({
        userId: selectedUser._id,
        packId: selectedPack._id,
        isPaid: isFree || markAsPaid,
        basePrice: !isFree ? (parseFloat(basePrice) || selectedPack.totalPrice || 0) : 0,
        taxes: 0,
        modeOfPayment: paymentMode,
        appSource,
        startDate: startDate || undefined,
        ...(overrideLimit ? { overrideLimit: true } : {}),
        // Pinned to the pack's own location — under "All locations" the list
        // spans every location and the switcher sends __all__, which the
        // server refuses for writes.
      }, studioIdOf(selectedPack.studio) || pinStudioId);
      setAssignSuccess(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1500);
    } catch (err: any) {
      if (err?.code === 'PACK_PURCHASE_LIMIT_REACHED' && err?.overridable && !overrideLimit) {
        setLimitWarning(err.message || 'This member has already reached the purchase limit for this pack.');
      } else {
        setAssignError(err?.response?.data?.message || err.message || 'Failed to assign pack');
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const getUserInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return parts[0]?.[0]?.toUpperCase() || 'U';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Assign Pack to User</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {assignSuccess ? (
          <Box textAlign="center" py={3}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Check sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="success.main">Pack Assigned!</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Pack assigned and receipt emailed to the user.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              <Step><StepLabel icon={<PersonAdd fontSize="small" />}>Find User</StepLabel></Step>
              <Step><StepLabel icon={<Inventory fontSize="small" />}>Select Pack</StepLabel></Step>
              {showSourceStep && <Step><StepLabel icon={<Source fontSize="small" />}>Source</StepLabel></Step>}
              <Step><StepLabel icon={<Payment fontSize="small" />}>Payment</StepLabel></Step>
            </Stepper>

            {/* Super admin studio selector */}
            {isSuperAdmin && Array.isArray(studios) && studios.length > 0 && (
              <Box mb={3}>
                <Autocomplete
                  options={studios}
                  getOptionLabel={(s) => s.studioName}
                  value={selectedStudio}
                  onChange={(_, val) => {
                    // Only reset studio-dependent selections (packs) — changing
                    // studio must never unselect the already-picked user.
                    setSelectedStudio(val);
                    setPacks([]);
                    setSelectedPack(null);
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Studio" size="small" />}
                />
              </Box>
            )}

            {mustPickBranch && (
              <Box mb={3}>
                <BranchSelect
                  value={branch}
                  onChange={(v) => {
                    // Same rule as the studio picker above: packs follow the
                    // location, the already-picked user stays.
                    setBranch(v);
                    packsReqRef.current++; // drop any in-flight load for the old one
                    setPacks([]);
                    setSelectedPack(null);
                  }}
                  minRole="manager"
                />
                {!branch && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Choose which location this pack is assigned at.
                  </Alert>
                )}
              </Box>
            )}

            {/* ── Step 1: Find / Create User ── */}
            <Box mb={3}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Step 1: Find User</Typography>
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  label="Phone Number"
                  placeholder="XXXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  size="small"
                  fullWidth
                  disabled={!!selectedUser}
                  onKeyDown={(e) => e.key === 'Enter' && handleCheckUser()}
                  helperText={phone.trim() && !isValidPhoneNumber(phone) ? 'Enter a valid 10-digit phone number' : undefined}
                  error={Boolean(phone.trim()) && !isValidPhoneNumber(phone)}
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>+91</Typography>
                    ),
                  }}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                />
                <Button
                  variant="contained"
                  onClick={handleCheckUser}
                  disabled={isCheckingUser || !isValidPhoneNumber(phone) || !!selectedUser}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isCheckingUser ? <CircularProgress size={18} color="inherit" /> : 'Search'}
                </Button>
              </Box>

              {!selectedUser && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1 }}>
                    — or search by name —
                  </Typography>
                  <Autocomplete
                    options={nameSearchResults}
                    getOptionLabel={(u) => `${u.name || ''}`}
                    filterOptions={(x) => x}
                    inputValue={nameQuery}
                    onInputChange={(_, val, reason) => { if (reason !== 'reset') setNameQuery(val); }}
                    onChange={(_, val) => {
                      if (val) {
                        setSelectedUser(val);
                        setNameQuery('');
                        setNameSearchResults([]);
                        setUserCheckResult(null);
                      }
                    }}
                    loading={isSearchingByName}
                    noOptionsText={nameQuery.length >= 2 ? 'No users found' : 'Type at least 2 characters'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search by name"
                        size="small"
                        placeholder="Type name..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isSearchingByName ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option._id}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{option.name || 'Unknown'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.phoneNumber || option.email || ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </>
              )}

              {selectedUser && (
                <Box display="flex" alignItems="center" gap={1.5} p={1.5} bgcolor="action.hover" borderRadius={1}>
                  <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: 'primary.main' }}>
                    {getUserInitials(selectedUser.name)}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>{selectedUser.name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedUser.phoneNumber}</Typography>
                  </Box>
                  <Button size="small" onClick={() => { setSelectedUser(null); setUserCheckResult(null); setSelectedPack(null); setNameQuery(''); setNameSearchResults([]); }}>
                    Change
                  </Button>
                </Box>
              )}

              {userCheckResult && !userCheckResult.exists && !selectedUser && (
                <Box mt={2} p={2} bgcolor="warning.lighter" borderRadius={1} border="1px solid" borderColor="warning.light">
                  <Typography variant="body2" color="warning.dark" fontWeight={600} mb={1.5}>
                    User not found. Create new user:
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <TextField label="Full Name" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} size="small" fullWidth required />
                    <TextField label="Email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} size="small" fullWidth required />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Gender (optional)</InputLabel>
                      <Select value={newUserGender} label="Gender (optional)" onChange={(e) => setNewUserGender(e.target.value as string)}>
                        <MenuItem value="">Prefer not to say</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    {createUserError && <Alert severity="error" sx={{ py: 0 }}>{createUserError}</Alert>}
                    <Button
                      variant="contained"
                      onClick={handleCreateUser}
                      disabled={isCreatingUser || !newUserName.trim() || !newUserEmail.trim()}
                      startIcon={isCreatingUser ? <CircularProgress size={14} color="inherit" /> : <PersonAdd />}
                    >
                      Create User
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ── Step 2: Select Pack ── */}
            {selectedUser && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Step 2: Select Pack</Typography>
                  {isLoadingPacks ? (
                    <Box display="flex" alignItems="center" gap={1} py={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">Loading packs…</Typography>
                    </Box>
                  ) : packs.length === 0 ? (
                    <Alert severity="warning">No active packs found for this studio.</Alert>
                  ) : (
                    <Autocomplete
                      options={packs}
                      getOptionLabel={(p) => `${p.title}${p.totalClasses ? ` (${p.totalClasses} sessions)` : ''}${p.totalPrice ? ` — ₹${p.totalPrice}` : ''}`}
                      value={selectedPack}
                      onChange={(_, val) => {
                        setSelectedPack(val);
                        if (val?.totalPrice) setBasePrice(String(val.totalPrice));
                      }}
                      renderInput={(params) => <TextField {...params} label="Select Pack" size="small" />}
                    />
                  )}
                </Box>
              </>
            )}

            {/* ── Step 3: Source (whitelabel app or booking-website studios) ── */}
            {selectedUser && selectedPack && showSourceStep && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Step 3: Assign From</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    {websiteOnly
                      ? 'Where should the member see this pack — on the studio’s booking website or in the SweatSocial app?'
                      : 'Where is this pack being assigned from? This determines revenue attribution.'}
                  </Typography>
                  <ToggleButtonGroup
                    value={appSource}
                    exclusive
                    onChange={(_, val) => val && setAppSource(val)}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value={ownSource!} sx={{ flexDirection: 'column', py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>{ownSourceLabel}</Typography>
                      <Typography variant="caption" color="text.secondary">{websiteOnly ? 'Booking website' : 'Whitelabel app'}</Typography>
                    </ToggleButton>
                    <ToggleButton value={SS_SOURCE} sx={{ flexDirection: 'column', py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>Sweat Social</Typography>
                      <Typography variant="caption" color="text.secondary">SweatSocial platform</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Box mt={1} p={1} bgcolor="action.hover" borderRadius={1}>
                    <Typography variant="caption" color="text.secondary">
                      {websiteOnly
                        ? appSource !== SS_SOURCE
                          ? `Pack shows on ${ownSourceLabel}’s booking website. Receipt sent with ${receiptBrandName} branding.`
                          : `Pack shows in the SweatSocial app, not on the booking website. Receipt sent with ${receiptBrandName} branding.`
                        : appSource !== SS_SOURCE
                          ? `Revenue tracked under ${whitelabelAppName}. Receipt sent with ${receiptBrandName} branding.`
                          : `Revenue tracked under SweatSocial. Receipt sent with ${receiptBrandName} branding.`}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* ── Step 4: Payment ── */}
            {selectedUser && selectedPack && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {showSourceStep ? 'Step 4' : 'Step 3'}: Payment
                  </Typography>

                  <Box p={2} bgcolor="action.hover" borderRadius={1} mb={2}>
                    <Typography variant="body2" fontWeight={600}>{selectedPack.title}</Typography>
                    {selectedPack.totalClasses != null && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedPack.totalClasses} sessions
                        {selectedPack.durationMonths ? ` · ${selectedPack.durationMonths} month${selectedPack.durationMonths > 1 ? 's' : ''}` : ''}
                      </Typography>
                    )}
                    {selectedPack.totalPrice != null && (
                      <Typography variant="body2" fontWeight={700} color="primary.main" mt={0.5}>
                        ₹{selectedPack.totalPrice.toLocaleString('en-IN')}
                      </Typography>
                    )}
                  </Box>

                  {paymentMode !== 'free' && (
                    <MarkAsPaidCheckbox checked={markAsPaid} onChange={setMarkAsPaid} />
                  )}

                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentMode}
                      label="Payment Method"
                      onChange={(e) => setPaymentMode(e.target.value as 'free' | 'cash' | 'online')}
                    >
                      <MenuItem value="free">Free / Complimentary</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="online">Online / UPI</MenuItem>
                    </Select>
                  </FormControl>

                  {paymentMode !== 'free' && (
                    <TextField
                      label="Amount Collected (₹)"
                      value={basePrice}
                      onChange={(e) => setBasePrice(e.target.value)}
                      onWheel={stopWheelNudge}
                      size="small"
                      type="number"
                      fullWidth
                      sx={{ mb: 2 }}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  )}

                  <TextField
                    label="Pack Start Date (optional)"
                    helperText="Leave blank to start today. Past dates will back-date the pack and adjust expiry accordingly."
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    size="small"
                    type="date"
                    fullWidth
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />

                  <Box p={1.5} bgcolor="info.lighter" borderRadius={1} mb={2}>
                    <Typography variant="caption" color="info.dark">
                      Receipt email will be sent to <strong>{selectedUser.email || selectedUser.phoneNumber}</strong> using{' '}
                      <strong>{receiptBrandName}</strong> branding.
                    </Typography>
                  </Box>

                  <div ref={assignAlertRef} />
                  {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}
                  {limitWarning && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2 }}
                      action={
                        <Button color="inherit" size="small" disabled={isAssigning} onClick={() => handleAssignPack(true)}>
                          Assign anyway
                        </Button>
                      }
                    >
                      {limitWarning}
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {!assignSuccess && (
        <DialogActions sx={{ px: 3, pb: 3, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
          <Button onClick={onClose} disabled={isAssigning}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleAssignPack()}
            disabled={!selectedUser || !selectedPack || isAssigning}
            startIcon={isAssigning ? <CircularProgress size={16} color="inherit" /> : <Check />}
          >
            Assign Pack
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ManualPackAssignDialog;
